import { createContext, useContext, type ReactNode } from "react"

/**
 * The native window an application runs in when it is hosted as a desktop app.
 * Design-system parts use it for window chrome (dragging, maximizing, window controls);
 * in a browser there is no desktop window and those parts stay inert.
 */
export interface DesktopWindow {
  startDragging(): Promise<void>
  toggleMaximize(): Promise<void>
  minimize(): Promise<void>
  close(): Promise<void>
  isMaximized(): Promise<boolean>
  /** Resolves to an unsubscribe function. */
  onResized(listener: () => void): Promise<() => void>
}

const DesktopWindowContext = createContext<DesktopWindow | null>(null)

/** Provides the hosting desktop window; pass `null` (or omit the provider) in a browser. */
export function DesktopWindowProvider({
  window,
  children,
}: {
  readonly window: DesktopWindow | null
  readonly children: ReactNode
}) {
  return <DesktopWindowContext.Provider value={window}>{children}</DesktopWindowContext.Provider>
}

/** The hosting desktop window, or `null` when the application runs in a browser. */
export function useDesktopWindow(): DesktopWindow | null {
  return useContext(DesktopWindowContext)
}
